import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { FiMoon, FiSun, FiGlobe, FiClock, FiBell } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import * as authService from '../../services/auth';
import '../../styles/main.scss';

const Settings = () => {
  const { user, updateUser } = useAuth();

  const [preferences, setPreferences] = useState({
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    notifications: {
      email: true,
      push: true,
      documentAssignments: true,
      comments: true,
      statusChanges: true,
    },
  });

  useEffect(() => {
    if (user && user.preferences) {
      setPreferences({
        theme: user.preferences.theme || 'light',
        language: user.preferences.language || 'en',
        timezone: user.preferences.timezone || 'UTC',
        notifications: {
          email:
            user.preferences.notifications &&
            user.preferences.notifications.email !== undefined
              ? user.preferences.notifications.email
              : true,
          push:
            user.preferences.notifications &&
            user.preferences.notifications.push !== undefined
              ? user.preferences.notifications.push
              : true,
          documentAssignments:
            user.preferences.notifications &&
            user.preferences.notifications.documentAssignments !== undefined
              ? user.preferences.notifications.documentAssignments
              : true,
          comments:
            user.preferences.notifications &&
            user.preferences.notifications.comments !== undefined
              ? user.preferences.notifications.comments
              : true,
          statusChanges:
            user.preferences.notifications &&
            user.preferences.notifications.statusChanges !== undefined
              ? user.preferences.notifications.statusChanges
              : true,
        },
      });
    }
  }, [user]);

  const updateSettingsMutation = useMutation(
    (payload) => authService.updateProfile(payload),
    {
      onSuccess: (data) => {
        const updatedUser = data && (data.data || data.user || data);
        if (updatedUser) {
          updateUser(updatedUser);
        }
        toast.success(
          (data && data.message) || 'Settings updated successfully'
        );
      },
      onError: (error) => {
        toast.error(
          (error && error.message) || 'Failed to update settings'
        );
      },
    }
  );

  const handlePreferenceChange = (field, value) => {
    setPreferences((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNotificationChange = (field, value) => {
    setPreferences((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: value,
      },
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    updateSettingsMutation.mutate({
      preferences,
    });
  };

  return (
    <div className="dashboard-page settings-page">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Manage your personal preferences and notifications</p>
        </div>
      </div>

      <div className="page-content">
        <form onSubmit={handleSubmit}>
          <div className="profile-layout">
            <motion.div
              className="profile-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>Theme</h2>
              <p>Choose how the interface looks on your device</p>

              <div className="form-group">
                <label>Appearance</label>
                <div className="form-row">
                  <button
                    type="button"
                    className={
                      preferences.theme === 'light'
                        ? 'btn btn-primary'
                        : 'btn btn-outline'
                    }
                    onClick={() => handlePreferenceChange('theme', 'light')}
                  >
                    <FiSun />
                    <span>Light</span>
                  </button>
                  <button
                    type="button"
                    className={
                      preferences.theme === 'dark'
                        ? 'btn btn-primary'
                        : 'btn btn-outline'
                    }
                    onClick={() => handlePreferenceChange('theme', 'dark')}
                  >
                    <FiMoon />
                    <span>Dark</span>
                  </button>
                  <button
                    type="button"
                    className={
                      preferences.theme === 'auto'
                        ? 'btn btn-primary'
                        : 'btn btn-outline'
                    }
                    onClick={() => handlePreferenceChange('theme', 'auto')}
                  >
                    <FiClock />
                    <span>Auto</span>
                  </button>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="profile-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <h2>Notifications</h2>
              <p>Select which notifications you want to receive</p>

              <div className="form-group">
                <label>
                  <FiBell /> Channels
                </label>
                <div className="form-row">
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={preferences.notifications.email}
                      onChange={(event) =>
                        handleNotificationChange(
                          'email',
                          event.target.checked
                        )
                      }
                    />
                    <span>Email</span>
                  </label>
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={preferences.notifications.push}
                      onChange={(event) =>
                        handleNotificationChange(
                          'push',
                          event.target.checked
                        )
                      }
                    />
                    <span>In-app</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Events</label>
                <div className="form-row">
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={preferences.notifications.documentAssignments}
                      onChange={(event) =>
                        handleNotificationChange(
                          'documentAssignments',
                          event.target.checked
                        )
                      }
                    />
                    <span>Document assignments</span>
                  </label>
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={preferences.notifications.comments}
                      onChange={(event) =>
                        handleNotificationChange(
                          'comments',
                          event.target.checked
                        )
                      }
                    />
                    <span>Comments</span>
                  </label>
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={preferences.notifications.statusChanges}
                      onChange={(event) =>
                        handleNotificationChange(
                          'statusChanges',
                          event.target.checked
                        )
                      }
                    />
                    <span>Status changes</span>
                  </label>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="profile-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2>Localization</h2>
              <p>Set your preferred language and time zone</p>

              <div className="form-group">
                <label>
                  <FiGlobe /> Language
                </label>
                <select
                  className="form-control"
                  value={preferences.language}
                  onChange={(event) =>
                    handlePreferenceChange('language', event.target.value)
                  }
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  <FiClock /> Time zone
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={preferences.timezone}
                  onChange={(event) =>
                    handlePreferenceChange('timezone', event.target.value)
                  }
                  placeholder="e.g. UTC, Europe/London"
                />
              </div>
            </motion.div>
          </div>

          <div className="form-actions" style={{ marginTop: '1.5rem' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={updateSettingsMutation.isLoading}
            >
              {updateSettingsMutation.isLoading ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
